<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import type { RequestFact } from '../../utils/requestDetailViewUtils'

// One titled group of facts on the request-detail screen — "PROPERTY DETAILS",
// "PRICE OFFER", "WHAT YOU'LL RECEIVE" — as the design draws every one of them:
// an eyebrow with a hairline, then a three-column grid of icon · label · value.
// The default slot renders under the grid (the owners table, a step row).
defineProps<{
  title: string
  facts: RequestFact[]
}>()
</script>

<template>
  <Box direction="col" gap="4" class="facts">
    <Box flex align="center" gap="3">
      <Text
        is="h3"
        type="caption-sm"
        weight="semibold"
        transform="uppercase"
        color="var(--p-sliceal-text-subtle)"
        class="facts__title"
      >
        {{ title }}
      </Text>
      <Box grow class="facts__rule" aria-hidden="true" />
    </Box>

    <Box v-if="facts.length" is="dl" class="facts__grid">
      <Box
        v-for="fact in facts"
        :key="fact.label"
        flex
        align="start"
        gap="3"
        class="facts__fact"
        :class="{ 'facts__fact--wide': fact.isWide }"
      >
        <Box flex align="center" justify="center" class="facts__icon" aria-hidden="true">
          <PiIcon :name="fact.icon" class="facts__glyph" />
        </Box>
        <Box direction="col" gap="0.125rem" grow class="facts__text">
          <Text is="dt" type="caption">{{ fact.label }}</Text>
          <Text is="dd" type="body" weight="semibold" class="facts__value">
            {{ fact.value }}
          </Text>
        </Box>
      </Box>
    </Box>

    <slot />
  </Box>
</template>

<style scoped>
.facts__title {
  flex-shrink: 0;
  margin: 0;
  letter-spacing: 0.66px;
}

.facts__rule {
  height: 1px;
  background: var(--p-surface-100);
}

.facts__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.375rem 1.5rem;
  margin: 0;
}

.facts__fact {
  min-width: 0;
}

.facts__fact--wide {
  grid-column: 1 / -1;
}

.facts__icon {
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 8px;
  background: var(--p-surface-100);
  color: var(--p-text-muted-color);
}

.facts__glyph {
  font-size: 0.875rem;
}

.facts__text {
  min-width: 0;
  word-break: break-word;
}

.facts__value {
  margin: 0;
  line-height: 20px;
}

@media (max-width: 1200px) {
  .facts__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .facts__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
